<app-page-breadcrumb pageTitle="Programación de Monedas" />

<div class="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 shadow-sm">
  <div class="p-6">
    <!-- Header -->
    <div class="mb-4 flex items-center justify-between">
      <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Jobs de Actualización de Tasas</h2>
      <button
        (click)="openCreate()"
        class="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 transition-colors">
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
        </svg>
        Nuevo Job
      </button>
    </div>

    <!-- Loading -->
    @if (loading) {
      <div class="flex items-center justify-center py-12">
        <div class="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-500"></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando...</span>
      </div>
    }

    <!-- Error -->
    @if (error) {
      <div class="mb-4 rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-500/20 dark:bg-error-500/10">
        <p class="text-sm text-error-600 dark:text-error-400">{{ error }}</p>
      </div>
    }

    <!-- Table -->
    @if (!loading && !error) {
      @if (jobs.length === 0) {
        <div class="flex flex-col items-center justify-center py-16 text-center">
          <svg class="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p class="text-sm text-gray-500 dark:text-gray-400">No hay jobs configurados.</p>
          <button (click)="openCreate()" class="mt-3 text-sm text-brand-500 hover:text-brand-600">Crear el primero</button>
        </div>
      } @else {
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th class="py-3 px-4">Descripción</th>
                <th class="py-3 px-4">Frecuencia</th>
                <th class="py-3 px-4">Reintentos</th>
                <th class="py-3 px-4">Estado</th>
                <th class="py-3 px-4 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
              @for (j of jobs; track j.id) {
                <tr class="transition hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td class="py-3 px-4 font-medium text-gray-800 dark:text-white">{{ j.description ?? '—' }}</td>
                  <td class="py-3 px-4 text-gray-600 dark:text-gray-300 text-xs">{{ getFrequencyLabel(j) }}</td>
                  <td class="py-3 px-4 text-gray-500 dark:text-gray-400 text-xs">{{ j.maxRetries }}</td>
                  <td class="py-3 px-4">
                    <button
                      (click)="toggleJob(j)"
                      class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none"
                      [class.bg-brand-500]="j.enabled"
                      [class.bg-gray-300]="!j.enabled"
                      [class.dark:bg-gray-600]="!j.enabled"
                      [title]="j.enabled ? 'Activo — clic para desactivar' : 'Inactivo — clic para activar'">
                      <span
                        class="inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform"
                        [class.translate-x-4]="j.enabled"
                        [class.translate-x-1]="!j.enabled">
                      </span>
                    </button>
                    <span class="ml-2 text-xs" [class.text-green-600]="j.enabled" [class.text-gray-400]="!j.enabled">
                      {{ j.enabled ? 'Activo' : 'Inactivo' }}
                    </span>
                  </td>
                  <td class="py-3 px-4 text-center">
                    <div class="flex items-center justify-center gap-2">
                      <button
                        (click)="runNow(j)"
                        [disabled]="runningJobId === j.id"
                        class="rounded-lg px-2.5 py-1 text-xs font-medium text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Ejecutar ahora">
                        @if (runningJobId === j.id) {
                          <span class="inline-block h-3 w-3 animate-spin rounded-full border-b-2 border-green-500"></span>
                        } @else {
                          Ejecutar
                        }
                      </button>
                      <button
                        (click)="openEdit(j)"
                        class="rounded-lg px-2.5 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-500/10 transition-colors">
                        Editar
                      </button>
                      <button
                        (click)="deleteJob(j)"
                        class="rounded-lg px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 transition-colors">
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    }
  </div>
</div>

<!-- Modal crear/editar. Inc 9: z-modal (no z-50) para que el overlay cubra también el header (z-header). -->
@if (showModal) {
  <div class="fixed inset-0 z-modal flex items-center justify-center bg-black/50 p-4">
    <div class="w-full max-w-lg rounded-2xl bg-white shadow-xl dark:bg-gray-900 max-h-[90vh] overflow-y-auto">
      <div class="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <h3 class="text-base font-semibold text-gray-800 dark:text-white">
          {{ editingJob ? 'Editar Job' : 'Nuevo Job' }}
        </h3>
        <button (click)="showModal = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <div class="p-6 space-y-4">
        <!-- Descripción -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Descripción</label>
          <input
            [(ngModel)]="formDescription"
            type="text"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            placeholder="Ej. Actualización automática de tasas de cambio">
        </div>
        <!-- Frecuencia -->
        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo de frecuencia</label>
          <div class="flex flex-wrap gap-2">
            @for (opt of [
              { value: 'HOURLY', label: 'Cada hora' },
              { value: 'DAILY', label: 'Diario' },
              { value: 'WEEKLY', label: 'Semanal' },
              { value: 'MONTHLY', label: 'Mensual' },
              { value: 'CUSTOM', label: 'Personalizado' }
            ]; track opt.value) {
              <button
                type="button"
                (click)="setFrequencyType(opt.value)"
                class="rounded-full px-3 py-1 text-sm font-medium border transition-colors"
                [class.bg-brand-500]="formFrequencyType === opt.value"
                [class.text-white]="formFrequencyType === opt.value"
                [class.border-brand-500]="formFrequencyType === opt.value"
                [class.border-gray-300]="formFrequencyType !== opt.value"
                [class.text-gray-600]="formFrequencyType !== opt.value"
                [class.hover:bg-gray-50]="formFrequencyType !== opt.value">
                {{ opt.label }}
              </button>
            }
          </div>
        </div>

        <!-- Controles por modo -->
        @if (formFrequencyType === 'HOURLY') {
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Cada X horas</label>
            <select
              [(ngModel)]="formIntervalValue"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white">
              @for (h of hourlyOptions; track h) {
                <option [value]="h">{{ h }} hora(s)</option>
              }
            </select>
          </div>
        }

        @if (formFrequencyType === 'DAILY') {
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Hora de ejecución</label>
            <input
              [(ngModel)]="formStartTime"
              type="time"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white">
          </div>
        }

        @if (formFrequencyType === 'WEEKLY') {
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Días de la semana</label>
            <div class="flex flex-wrap gap-2">
              @for (d of daysOfWeekOptions; track d.value) {
                <button
                  type="button"
                  (click)="toggleDay(d.value)"
                  class="rounded-full px-3 py-1 text-sm font-medium border transition-colors"
                  [class.bg-brand-500]="isDaySelected(d.value)"
                  [class.text-white]="isDaySelected(d.value)"
                  [class.border-brand-500]="isDaySelected(d.value)"
                  [class.border-gray-300]="!isDaySelected(d.value)"
                  [class.text-gray-600]="!isDaySelected(d.value)">
                  {{ d.label }}
                </button>
              }
            </div>
            <div class="mt-3">
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Hora</label>
              <input
                [(ngModel)]="formStartTime"
                type="time"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white">
            </div>
          </div>
        }

        @if (formFrequencyType === 'MONTHLY') {
          <div class="flex gap-3">
            <div class="flex-1">
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Día del mes</label>
              <input
                [(ngModel)]="formDayOfMonth"
                type="number"
                min="1"
                max="31"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white">
            </div>
            <div class="flex-1">
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Hora</label>
              <input
                [(ngModel)]="formStartTime"
                type="time"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white">
            </div>
          </div>
        }

        @if (formFrequencyType === 'CUSTOM') {
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Expresión CRON
              <span class="ml-1 text-xs font-normal text-gray-400">(seg min hora día-mes mes día-semana)</span>
            </label>
            <input
              [(ngModel)]="formCronExpression"
              type="text"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              placeholder="0 0 */6 * * *">
          </div>
        }

        <!-- Zona horaria -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Zona horaria</label>
          <select
            [(ngModel)]="formTimezone"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white">
            @for (tz of timezones; track tz) {
              <option [value]="tz">{{ tz }}</option>
            }
          </select>
        </div>

        <!-- Reintentos -->
        <div class="flex gap-3">
          <div class="flex-1">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Reintentos máx.</label>
            <input
              [(ngModel)]="formMaxRetries"
              type="number"
              min="0"
              max="10"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white">
          </div>
          @if (formMaxRetries > 0) {
            <div class="flex-1">
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Backoff (seg)</label>
              <input
                [(ngModel)]="formRetryBackoffSeconds"
                type="number"
                min="0"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white">
            </div>
          }
        </div>

        <!-- Vista previa -->
        <div class="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Vista previa</p>
          <p class="text-sm text-gray-700 dark:text-gray-200">{{ getPreviewText() }}</p>
        </div>
      </div>
      <div class="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
        <button
          (click)="showModal = false"
          class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800">
          Cancelar
        </button>
        <button
          (click)="save()"
          [disabled]="saving"
          class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {{ saving ? 'Guardando...' : 'Guardar' }}
        </button>
      </div>
    </div>
  </div>
}
